import type { ContractArchivePreview } from "@closer/shared";
import { Button, Checkbox, Modal, Skeleton, Textarea } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * ARŞİVLEME ONAYI (CLOS-122 `[104]`).
 *
 * ### ⚠ Arşivleme TEK TIKLA olmuyor
 *
 * Diğer durum geçişleri düğmeye basınca oluyor; arşivleme olmuyor. Kart: kullanıcıya
 * *"açık işlem özetini, ETKİ AÇIKLAMASINI ve güvenli sonuç yönlendirmesini"* sunmayı
 * istiyor. Sözleşmeyi akıştan çıkarmak geri alınabilir ama ucuz değil: kapasite serbest
 * kalıyor, kayıt aktif listelerden düşüyor. Kullanıcı ne olacağını GÖRMEDEN onaylamamalı.
 *
 * ### ⚠ Uyarı onayı KUTUCUKLA, sessiz değil
 *
 * *"Yalnız uyarı gerektiriyorsa etki açıkça gösterilir ve yetkili kullanıcı ONAYI
 * alınır."* Onay isteğin gövdesinde taşınıyor (`acknowledgeWarnings`) ve sunucu onaysız
 * isteği reddediyor — kutucuk bir nezaket değil, sözleşmenin parçası.
 *
 * ### ⚠ Engel varsa düğme YOK
 *
 * Engelleyici açık iş varsa arşivleme düğmesi hiç sunulmuyor: basınca 409 alacak bir
 * düğme, kullanıcıya sebebi okumadan denemeyi öğretirdi.
 */

export function ContractArchiveDialog({
  contractId,
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  contractId: string;
  open: boolean;
  onClose: () => void;
  onConfirm: (girdi: { reason: string; acknowledgeWarnings: boolean }) => void;
  isPending: boolean;
}) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const [reason, setReason] = useState("");
  const [onaylandi, setOnaylandi] = useState(false);

  const sorgu = useQuery({
    enabled: open,
    queryKey: tenantKey(tenantId, "contract", contractId, "arsiv-onizleme"),
    queryFn: async () =>
      (
        await api<ContractArchivePreview>(
          `/api/contracts/${contractId}/lifecycle/archive-preview`,
          { tenantId },
        )
      ).data,
  });

  const onizleme = sorgu.data;
  const uyariVar = (onizleme?.warnings.length ?? 0) > 0;
  const gonderilebilir = Boolean(onizleme?.canArchive) && (!uyariVar || onaylandi);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sözleşmeyi arşivle"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          {/* Engel varsa düğme hiç yok; kullanıcı sebebi okuyor. */}
          {onizleme?.canArchive && (
            <Button
              type="button"
              loading={isPending}
              disabled={!gonderilebilir}
              onClick={() => onConfirm({ reason: reason.trim(), acknowledgeWarnings: onaylandi })}
            >
              Arşivle
            </Button>
          )}
        </div>
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label="Arşiv etkileri yükleniyor">
          <Skeleton lines={4} />
        </div>
      ) : sorgu.error || !onizleme ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {onizleme.blocking.length > 0 && (
            <section className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-danger">Arşivlenemiyor</h3>
              <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg">
                {onizleme.blocking.map((i) => (
                  <li key={`${i.provider}-${i.message}`}>{i.message}</li>
                ))}
              </ul>
            </section>
          )}

          {uyariVar && (
            <section className="flex flex-col gap-1">
              <h3 className="text-sm font-semibold text-warning">Dikkat</h3>
              <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg">
                {onizleme.warnings.map((i) => (
                  <li key={`${i.provider}-${i.message}`}>{i.message}</li>
                ))}
              </ul>
            </section>
          )}

          {/* ⚠ Etkiler ŞEMADAN geliyor; ekran kendi cümlesini uydurmuyor (refleks 4). */}
          <section className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-fg-heading">Arşivleyince ne olur?</h3>
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-fg-soft">
              {onizleme.effects.map((etki) => (
                <li key={etki}>{etki}</li>
              ))}
            </ul>
          </section>

          {onizleme.canArchive && (
            <>
              <label className="flex flex-col gap-1 text-sm">
                <span className="text-fg-soft">Arşivleme nedeni (isteğe bağlı)</span>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Örn. Proje tamamlandı"
                />
              </label>

              {uyariVar && (
                <Checkbox
                  checked={onaylandi}
                  onChange={(e) => setOnaylandi(e.target.checked)}
                  label="Yukarıdaki uyarıları okudum, yine de arşivlemek istiyorum"
                />
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
